import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  // getRowModel,
  flexRender,
} from '@tanstack/react-table';
import Paginator from 'react-hooks-paginator';
import { Overlay, Popover, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Loader from './Loader';
// Import your custom paginator component:

export default function Table({
  data,
  columns, // array of column definitions
  enableGlobalFilter = true,
  enableSorting = true,
  enablePagination = true,
  enableSingleRowSelection = false,
  pageSizeOptions = [5, 10, 20],
  defaultPageSize = 10,
  setSelectedRow = () => {},
  searchPlaceholder = 'Search Record ...',
  isLoading = false,
  extras = null,
  getRowId = (_, index) => index,
  preselectedRowId,
  serverSideSorting = false,
  serverSideFiltering = false,
  onServerSideSortChange = () => {},
  onServerSideFilterChange = () => {},
  totalRecords = 0,
  initialPageIndex = 0,
  onServerSidePageChange = () => {},
}) {
  const isServerSidePaging = serverSideFiltering || serverSideSorting;
  const {t} =  useTranslation();
  // Local states for sorting, filtering, pagination, row selection
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [sorting, setSorting] = React.useState([]);
  const [columnFilters, setColumnFilters] = React.useState([]);
  const [pagination, setPagination] = React.useState({
    pageIndex: initialPageIndex, // start from 0
    pageSize: defaultPageSize,
  });
  const [rowSelection, setRowSelection] = React.useState(() =>
    preselectedRowId !== undefined ? { [preselectedRowId]: true } : {}
  );

  // Track whether each column’s filter-popover is open
  const [filterOpen, setFilterOpen] = React.useState({});
  // Local ephemeral values for each column’s filter input (so we don’t immediately call setFilterValue)
  const [localFilterValues, setLocalFilterValues] = React.useState({});

  const [offset, setOffset] = React.useState(
    initialPageIndex * defaultPageSize
  );

  const toggleFilter = (columnId, target) => {
    setFilterOpen((prev) => {
      const isCurrentlyOpen = !!prev[columnId]?.open;
      return {
        ...prev,
        [columnId]: {
          open: !isCurrentlyOpen,
          target: target || null,
        },
      };
    });
  };

  // Handler to update local ephemeral filter value
  const updateLocalFilterValue = (columnId, newValue) => {
    setLocalFilterValues((prev) => ({
      ...prev,
      [columnId]: newValue,
    }));
  };

  // Handler to apply filter on "Submit"
  const applyFilter = (columnId, column) => {
    // Finally set the real filter for that column
    column.setFilterValue(localFilterValues[columnId] ?? '');
    // If doing server-side filtering, this will trigger onColumnFiltersChange
    // through the table's onColumnFiltersChange
    // Then close the popover
    setFilterOpen((prev) => ({
      ...prev,
      [columnId]: { ...prev[columnId], open: false },
    }));
  };

  // Returns true if this column has an active filter
  const isColumnFiltered = (columnId) => {
    return columnFilters.some(
      (f) => f.id === columnId && f.value !== undefined && f.value !== ''
    );
  };

  const processedColumns = React.useMemo(() => {
    return columns.map((col) => {
      // By default, columns are sorted unless col.sorted = false
      const enableSortingForThisColumn = col.sorted === false ? false : true;

      // By default, columns are not filterable unless col.filter = true
      const enableColumnFilterForThisColumn = col.filter === true;

      return {
        ...col,
        // TanStack property that determines if it can be sorted
        enableSorting: enableSortingForThisColumn,
        // We'll store a custom flag for column filters
        enableColumnFilter: enableColumnFilterForThisColumn,
        // If user wants a custom filter UI for that column
        filterComponent: col.filterComponent || null,
      };
    });
  }, [columns]);

  const anyColumnHasFilter = processedColumns.some((c) => c.enableColumnFilter);

  // If you want single‐row selection, we’ll handle that manually in a custom "Select" column
  const columnsWithSelection = React.useMemo(() => {
    if (!enableSingleRowSelection) return processedColumns;

      return [
        {
          id: 'select',
          header: '',
          cell: ({ row }) => {
          // Determine if this row is selectable: requires phone and email
            const email = (row?.original?.email || '').trim();
            const phone = `${row?.original?.countryCode || ''}${row?.original?.areaCode || ''}${row?.original?.contactNumber || ''}`.trim();
            const isSelectable = Boolean(email) && Boolean(phone);

            return (
              <input
                type="radio"
                checked={!!rowSelection[row.id]}
                disabled={!isSelectable}
                title={!isSelectable ? t('Phone and Email are required') : ''}
                onChange={() => {
                  if (!isSelectable) return;
                  setRowSelection({ [row.id]: true });
                  setSelectedRow(row.id);
                }}
              />
            );
          },
          enableSorting: false,
          enableGlobalFilter: false,
        },
        ...processedColumns,
    ];
  }, [processedColumns, enableSingleRowSelection, rowSelection]);

  // Create the table instance
  const table = useReactTable({
    data,
    getRowId: getRowId,
    columns: columnsWithSelection,
    state: {
      sorting: enableSorting ? sorting : [],
      globalFilter: enableGlobalFilter ? globalFilter : '',
      // pagination: enablePagination
      //   ? pagination
      //   : { pageIndex: 0, pageSize: data.length },
      pagination: enablePagination
        ? pagination
        : { pageIndex: 0, pageSize: data.length },
      rowSelection,
      // columnFilters: anyColumnHasFilter ? columnFilters : [],
    },
    // onSortingChange: enableSorting ? setSorting : undefined,
    onSortingChange: (updater) => {
      // React Table gives the new sorting state
      const newSorting =
        typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(newSorting);

      // If we do serverSideSorting, notify the parent
      if (serverSideSorting && onServerSideSortChange) {
        onServerSideSortChange(newSorting);
      }
    },
    onColumnFiltersChange: anyColumnHasFilter
      ? (updater) => {
          const newFilters =
            typeof updater === 'function' ? updater(columnFilters) : updater;
          setColumnFilters(newFilters);

          // If serverSideFiltering is on, we call the parent
          if (serverSideFiltering && onServerSideFilterChange) {
            onServerSideFilterChange(newFilters);
          }
        }
      : undefined,
    onGlobalFilterChange: enableGlobalFilter ? setGlobalFilter : undefined,
    // onPaginationChange: enablePagination ? setPagination : undefined,
    onPaginationChange:
      enablePagination && !isServerSidePaging ? setPagination : undefined,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,

    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel:
      enableSorting && !serverSideSorting ? getSortedRowModel() : undefined,
    getFilteredRowModel:
      enableGlobalFilter && !serverSideFiltering
        ? getFilteredRowModel()
        : undefined,
    // getPaginationRowModel: enablePagination
    //   ? getPaginationRowModel()
    //   : undefined,
    getPaginationRowModel:
      enablePagination && !isServerSidePaging
        ? getPaginationRowModel()
        : undefined,
    // getRowModel: getRowModel(),
  });

  // Calculate pagination variables for custom Paginator

  // const effectiveTotalRecords = enablePagination
  //   ? isServerSidePaging
  //     ? totalRecords // from parent
  //     : table.getFilteredRowModel().rows.length // local
  //   : data.length;

  const effectiveTotalRecords = isServerSidePaging
    ? totalRecords
    : table.getFilteredRowModel().rows.length;

  const pageLimit = table.getState().pagination.pageSize;
  const currentPage = table.getState().pagination.pageIndex + 1;

  // const totalRecords = enablePagination
  //   ? table.getFilteredRowModel().rows.length
  //   : data.length;
  // const pageLimit = enablePagination
  //   ? table.getState().pagination.pageSize
  //   : data.length;
  // const currentPage = enablePagination
  //   ? table.getState().pagination.pageIndex + 1
  //   : 1;

  // Handler to update current page from Paginator
  // const setCurrentPageHandler = (page) => {
  //   table.setPageIndex(page - 1);
  // };
  const setCurrentPageHandler = (page) => {
    if (isServerSidePaging) {
      // Let the parent handle the actual data fetching
      // The parent can do something like fetch(pageIndex, pageSize)
      // Then set the table’s pagination pageIndex so the UI updates
      // Or you can store the pageIndex in your parent’s state
      onServerSidePageChange(page - 1, pageLimit);
      // update local pagination state so the paginator shows the correct page
      setPagination({ ...pagination, pageIndex: page - 1 });
    } else {
      // local pagination
      table.setPageIndex(page - 1);
    }
  };

  // Handler to update offset (offset = (currentPage - 1) * pageLimit)
  // const setOffsetHandler = (offset) => {
  //   const newPage = Math.floor(offset / pageLimit) + 1;
  //   table.setPageIndex(newPage - 1);
  // };

  const handleSetOffset = (newOffset) => {
    setOffset(newOffset);

    // Convert offset -> pageIndex
    const newPageIndex = Math.floor(newOffset / pageLimit);

    if (isServerSidePaging) {
      // Let parent fetch
      onServerSidePageChange(newPageIndex, pageLimit);
    } else {
      // local
      table.setPageIndex(newPageIndex);
    }
  };

  React.useEffect(() => {
    if (!isServerSidePaging) {
      const localIndex = table.getState().pagination.pageIndex;
      setOffset(localIndex * pageLimit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.getState().pagination.pageIndex, pageLimit, isServerSidePaging]);

  // ---- Separate Clear Functions ----
  const clearFilters = () => {
    // Clear column filters + global filter
    setColumnFilters([]);
    setGlobalFilter('');
    // Also clear ephemeral localFilterValues
    setLocalFilterValues({});
    // If server-side, notify parent
    if (serverSideFiltering && onServerSideFilterChange) {
      onServerSideFilterChange([]);
    }
  };

  const clearSorting = () => {
    setSorting([]);
    // If server-side, notify parent
    if (serverSideSorting && onServerSideSortChange) {
      onServerSideSortChange([]);
    }
  };

  return (
    <div>
      {/* Global Filter */}
      <div
        style={{
          display: 'flex',
          justifyContent: enableGlobalFilter ? 'space-between' : 'end',
          alignItems: 'center',
        }}
      >
        {enableGlobalFilter && (
          <div style={{ marginBottom: '1rem', maxWidth: '300px' }}>
            <input
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder={searchPlaceholder}
              style={{
                backgroundColor: '#fff',
                fontSize: '14px',
                height: '43px',
                padding: '2px 55px 2px 18px',
                color: '#000',
                border: '1px solid #e6e6e6',
                background: 'transparent none repeat scroll 0 0',
              }}
            />
          </div>
        )}
        {/* Buttons to clear filters and sorting separately */}
        {(serverSideFiltering || serverSideSorting) && (
          <div style={{ display: 'flex', gap: '10px', marginBottom: '5px' }}>
            {serverSideFiltering && (
              // <button
              //   style={{
              //     padding: '8px 16px',
              //     background: '#f2f2f2',
              //     border: '1px solid #ccc',
              //     cursor: 'pointer',
              //     borderRadius: '4px',
              //   }}
              //   onClick={clearFilters}
              // >
              //   Clear Filters
              // </button>
              <div className="btn-hover">
                <button
                  className="default-btn"
                  style={{
                    padding: '12px 25px 12px',
                    backgroundColor: '#F7F7F7',
                    color: '#000',
                  }}
                  onClick={clearFilters}
                >
                  {t("Clear Filters")}
                </button>
              </div>
            )}

            {serverSideSorting && (
              // <button
              //   style={{
              //     padding: '8px 16px',
              //     background: '#f2f2f2',
              //     border: '1px solid #ccc',
              //     cursor: 'pointer',
              //     borderRadius: '4px',
              //   }}
              //   onClick={clearSorting}
              // >
              //   Clear Sorting
              // </button>
              <div className="btn-hover">
                <button
                  className="default-btn"
                  style={{
                    padding: '10px 20px 12px',
                    backgroundColor: '#F7F7F7',
                    color: '#000',
                  }}
                  onClick={clearSorting}
                >
                  {t("Clear Sorting")}
                </button>
              </div>
            )}
          </div>
        )}
        {extras}
      </div>

      {/* Table */}
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <Loader loading={isLoading}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const canSort =
                      enableSorting &&
                      header.column.getCanSort() &&
                      !serverSideSorting;

                    // For overlay reference
                    const isFilterOpen = !!filterOpen[header.id]?.open;
                    const filterTarget = filterOpen[header.id]?.target;
                    const filteredActive = isColumnFiltered(header.id);

                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        style={{
                          borderBottom: '2px solid #ddd',
                          padding: '8px',
                          cursor: canSort ? 'pointer' : 'default',
                          backgroundColor: '#f8f8f8',
                          position: 'relative',
                        }}
                        onClick={
                          canSort
                            ? header.column.getToggleSortingHandler()
                            : undefined
                        }
                      >
                        {header.isPlaceholder
                          ? null
                          : typeof header.column.columnDef.header === 'function'
                          ? header.column.columnDef.header({
                              column: header.column,
                            })
                          : header.column.columnDef.header}

                        {/* Sort Icons */}
                        {header.column.getIsSorted() === 'asc' && (
                          <i className="fa fa-angle-up mx-1 text-black" />
                        )}
                        {header.column.getIsSorted() === 'desc' && (
                          <i className="fa fa-angle-down mx-1 text-black" />
                        )}

                        {/* Filter Icon, if columnFilter is enabled */}
                        {header.column.columnDef.enableColumnFilter && (
                          <>
                            <i
                              className="fa fa-filter"
                              style={{
                                marginLeft: '10px',
                                cursor: 'pointer',
                                color: filteredActive ? '#a749ff' : 'inherit',
                              }}
                              onClick={(e) => {
                                e.stopPropagation(); // Don’t trigger sorting
                                toggleFilter(header.id, e.target);
                              }}
                            />

                            <Overlay
                              show={isFilterOpen}
                              target={filterTarget}
                              placement="bottom"
                              containerPadding={5}
                            >
                              <Popover id={`filter-popover-${header.id}`}>
                                <Popover.Body>
                                  {/* 
                                  Container for the filter UI 
                                  + "Submit" button on bottom-right 
                                */}
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      minWidth: '150px',
                                    }}
                                  >
                                    {/* Scrollable filter content */}
                                    <div
                                      style={{
                                        maxHeight: '300px', // << Adjust as needed
                                        overflowY: 'auto',
                                        marginBottom: '8px',
                                        paddingRight: '4px', // to avoid overshadow by scrollbar
                                      }}
                                      className="custom-thin-scroll"
                                    >
                                      {(() => {
                                        // The user’s custom filter component
                                        const CustomFilter =
                                          header.column.columnDef
                                            .filterComponent;

                                        // Our ephemeral local value
                                        const localVal =
                                          localFilterValues[header.id] ?? '';

                                        const onLocalChange = (newVal) => {
                                          updateLocalFilterValue(
                                            header.id,
                                            newVal
                                          );
                                        };

                                        // If a custom filter, render it; otherwise, default text input
                                        if (CustomFilter) {
                                          return (
                                            <CustomFilter
                                              value={localVal}
                                              onChange={onLocalChange}
                                            />
                                          );
                                        } else {
                                          return (
                                            <input
                                              style={{
                                                width: '100%',
                                                padding: '4px',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                              }}
                                              placeholder={`Type filter...`}
                                              value={localVal}
                                              onChange={(e) =>
                                                onLocalChange(e.target.value)
                                              }
                                            />
                                          );
                                        }
                                      })()}
                                    </div>

                                    {/* Submit button container */}
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                      }}
                                    >
                                      <button
                                        onClick={() =>
                                          applyFilter(header.id, header.column)
                                        }
                                        style={{
                                          padding: '6px 12px',
                                          border: 'none',
                                          borderRadius: '4px',
                                          cursor: 'pointer',
                                        }}
                                      >
                                        {t("Submit")}
                                      </button>
                                    </div>
                                  </div>
                                </Popover.Body>
                              </Popover>
                            </Overlay>
                          </>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows?.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <td
                          key={cell.id}
                          style={{
                            borderBottom: '1px solid #eee',
                            padding: '8px',
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={columnsWithSelection.length}
                    style={{ textAlign: 'center', padding: '20px' }}
                  >
                    <div className="row">
                      <div className="col-lg-12">
                        <div className="item-empty-area text-center">
                          <div className="item-empty-area__icon mb-30">
                            <i className="pe-7s-search"></i>
                          </div>
                          <div className="item-empty-area__text">
                            {t("No record found")}
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Loader>
      </div>

      {/* Custom Pagination Controls */}
      {enablePagination && (
        <div className="pro-pagination-style text-center mt-30">
          <Paginator
            totalRecords={effectiveTotalRecords}
            pageLimit={pageLimit}
            pageNeighbours={2}
            offset={offset}
            setOffset={handleSetOffset}
            currentPage={currentPage}
            setCurrentPage={setCurrentPageHandler}
            pageContainerClass="mb-0 mt-0"
            pagePrevText="«"
            pageNextText="»"
          />
        </div>
      )}
    </div>
  );
}
